import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import AppShell from "./components/AppShell";
import FieldShell from "./components/FieldShell";

// Light pages (always loaded — keeps initial bundle responsive)
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import MyDay from "./pages/MyDay";
import FieldJobDetail from "./pages/FieldJobDetail";
import FieldSignOff from "./pages/FieldSignOff";

// Heavy office pages — lazy split (Pass D2a perf)
const Leads = React.lazy(() => import("./pages/Leads"));
const LeadDetail = React.lazy(() => import("./pages/LeadDetail"));
const Customers = React.lazy(() => import("./pages/Customers"));
const Projects = React.lazy(() => import("./pages/Projects"));
const ProjectWorkspace = React.lazy(() => import("./pages/ProjectWorkspace"));
const QuoteBuilder = React.lazy(() => import("./pages/QuoteBuilder"));
const PriceList = React.lazy(() => import("./pages/PriceList"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Schedule = React.lazy(() => import("./pages/Schedule"));
const Crews = React.lazy(() => import("./pages/Crews"));
const Today = React.lazy(() => import("./pages/Today"));
const Resources = React.lazy(() => import("./pages/Resources"));
const Signals = React.lazy(() => import("./pages/Signals"));
const Delays = React.lazy(() => import("./pages/Delays"));
const Audit = React.lazy(() => import("./pages/Audit"));
const Intelligence = React.lazy(() => import("./pages/Intelligence"));
const OpsHealth = React.lazy(() => import("./pages/OpsHealth"));
const Surveys = React.lazy(() => import("./pages/Surveys"));
const SurveyWizard = React.lazy(() => import("./pages/SurveyWizard"));
const Quotes = React.lazy(() => import("./pages/Quotes"));
const QuoteSuggestionsReview = React.lazy(() => import("./pages/QuoteSuggestionsReview"));
const Pipeline = React.lazy(() => import("./pages/Pipeline"));
const RevenueCommand = React.lazy(() => import("./pages/RevenueCommand"));
const FollowUpQueue = React.lazy(() => import("./pages/FollowUpQueue"));
const Deposits = React.lazy(() => import("./pages/Deposits"));
const Overview = React.lazy(() => import("./pages/Overview"));
const Chase = React.lazy(() => import("./pages/Chase"));
const OperationsHome = React.lazy(() => import("./pages/OperationsHome"));

function PageFallback() {
  return (
    <div
      data-testid="route-fallback"
      style={{
        height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        color: "var(--ink-muted)", fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase",
      }}
    >
      Loading…
    </div>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <PageFallback />;
  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search, hash: location.hash } }}
      />
    );
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return null;
  if (user) {
    const from = location.state?.from;
    const dest = from ? `${from.pathname || "/"}${from.search || ""}${from.hash || ""}` : "/";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function RootRouter() {
  const { user, ready } = useAuth();
  if (!ready) return <PageFallback />;
  if (user?.role === "crew_member") {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route element={<Protected><FieldShell /></Protected>}>
            <Route path="/" element={<MyDay />} />
            <Route path="/jobs" element={<MyDay />} />
            <Route path="/jobs/:blockId" element={<FieldJobDetail />} />
            <Route path="/jobs/:blockId/sign-off" element={<FieldSignOff />} />
            <Route path="/visit/:visitId" element={<MyDay />} />
            <Route path="/surveys" element={<Surveys />} />
            <Route path="/surveys/new" element={<SurveyWizard />} />
            <Route path="/surveys/:id" element={<SurveyWizard />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
        <Route element={<Protected><AppShell /></Protected>}>
          <Route path="/" element={<OperationsHome />} />
          <Route path="/home" element={<OperationsHome />} />
          <Route path="/overview" element={<Overview />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/pipeline" element={<Pipeline />} />
          <Route path="/revenue" element={<RevenueCommand />} />
          <Route path="/follow-ups" element={<FollowUpQueue />} />
          <Route path="/deposits" element={<Deposits />} />
          <Route path="/chase" element={<Chase />} />
          <Route path="/today" element={<Today />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/crews" element={<Crews />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/signals" element={<Signals />} />
          <Route path="/delays" element={<Delays />} />
          <Route path="/intelligence" element={<Intelligence />} />
          <Route path="/ops-health" element={<OpsHealth />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/leads" element={<Leads />} />
          <Route path="/leads/:id" element={<LeadDetail />} />
          <Route path="/customers" element={<Customers />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectWorkspace />} />
          <Route path="/projects/:id/quote/:quoteId?" element={<QuoteBuilder />} />
          <Route path="/quotes" element={<Quotes />} />
          <Route path="/surveys" element={<Surveys />} />
          <Route path="/surveys/new" element={<SurveyWizard />} />
          <Route path="/surveys/:id/create-quote" element={<QuoteSuggestionsReview />} />
          <Route path="/surveys/:id" element={<SurveyWizard />} />
          <Route path="/pricelist" element={<PriceList />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#1A1E24",
            border: "1px solid rgba(255,255,255,0.08)",
            color: "#F4F5F7",
          },
        }}
      />
      <BrowserRouter>
        <RootRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
